import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, switchMap, filter } from 'rxjs/operators';

import { mergeDeep, intercect, shrink } from '@library/common/functions';

@Injectable({
   providedIn: 'root',
})
export class Preferences {
   private url = 'user/preferences';

   private _prefs = {};
   private defaults = {
      currency: 'USD',
      pageSize: { default: 10 },
   };

   refresh$: Subject<void> = new Subject();
   private save: ReplaySubject<void> = new ReplaySubject();
   listen = false;

   get prefs() { return this._prefs; }
   set prefs( value ) {
      if (value == undefined) {
         // do nothing
      } else {
         this._prefs = value;
         sessionStorage.setItem( 'prefs', JSON.stringify( value ));
      }
   }

   /**
    *  Gets a pref from the pref store
    *  @param {string} component path to pref, can use object notation eg foo.bar.baz
    */
   getPref( component: string ): any {
      return component.split( '.' ).reduce(( a, c ) => a?.[c], mergeDeep( this.defaults, this.prefs ));
   }

   /**
    * Sets a pref in the pref store
    * @param {object} pref the pref to be set
    */
   setPref( pref: {[ key: string ]: any } ): void {
      const current = JSON.stringify( this.prefs );

      console.log( 'setPref', this.prefs, pref, this.defaults );
      this.prefs = intercect({ ...this.prefs, ...pref }, this.defaults );


      if ( JSON.stringify( this.prefs ) !== current ) {
         this.save.next();
      }
   }

   setPrefDefault( component: string, prop: object | string ) {
      if ( this.defaults[component] ) {
         console.warn( `Pref Default ${component} already set to ${JSON.stringify( this.defaults[component] )}` );

         return;
      }

      this.defaults[component] = prop;
   }

   upsertPref( component: string, prop: any ): void {
      console.log( 'upsertPref', component, prop );
      const updateObj = component.split( '.' ).reverse().reduce(( a, c ) => ({ [c]: a }), prop );
      console.log( 'updateObj', updateObj );

      this.setPref( mergeDeep( this.prefs, updateObj ));
   }

   clearPref( component: string, property?: string ): void {
      console.log( 'clearPref', component, property );
      const path = component.split( '.' );
      if ( property ) { path.push( property ); }

      console.log( 'path', path );

      path.reduce(( a, c, i ) => {
         if (( i === path.length - 1 ) && a?.[c] ) { delete a[c]; }

         return a?.[c];
      }, this.prefs );

      this.prefs = shrink( this.prefs );
      this.save.next();
   }

   getPageSize( component: string ): number {
      if ( this.prefs[component]?.pageSize ) {
         // remove old structure
         this.setPageSize( component, this.prefs[component]?.pageSize );
         this.clearPref( component, 'pageSize' );
      }

      return this.prefs?.['pageSize']?.[component] ?? this.defaults.pageSize.default;
   }

   setPageSize( component: string, size: number ): void {
      if ( size === this.defaults?.pageSize?.default ) {
         return this.clearPref( 'pageSize', component );
      }

      this.setPref({ pageSize: {[ component ]: size }});
   }

   constructor( private http: HttpClient ) {
      if ( localStorage.getItem( 'prefs' )) {
         // changing location of prefs to session
         localStorage.removeItem( 'prefs' );
      }

      this.save.pipe(
         debounceTime( 1000 ),
         filter( _ => !!this.listen ),
         switchMap( _ => this.http.post( this.url, this.prefs, { params: { useLambda: true } } )),
      ).subscribe( _ => this.refresh$.next() );
   }

   reset() {
      this.listen = false;
   }
}
